import round from 'lodash/round';
import type { Moment } from 'moment/moment';

import { MEMBERSHIP_PRESTATIONS_BASED_SURCHARGE, PRESTATIONS_BASED_SURCHARGE } from '../constants/prices';
import type {
  BasketContent,
  ComboPrice,
  GiftCard,
  Promotion,
  Universe,
  WellnessBasketPrices,
  ZoneId,
} from '../types/commons';
import { basketContentPrices, calculatePromoAndGiftValues, getAppointmentSurchargeReason } from './common-prices';

export const DEFAULT_WELLNESS_BASKET_PRICES: WellnessBasketPrices = {
  kind: 'wellness',
  comboPriceReduction: 0,
  basketContentWithPrices: [],
  totalPrice: 0,
  minPriceSurcharge: 0,
  tipAmount: 0,
  membership: {
    reduction: 0,
    totalPrice: 0,
    minPriceSurcharge: 0,
    appointmentSurcharge: 0,
    promoValue: 0,
    giftValue: 0,
    comboPriceReduction: 0,
  },
};

type ComputeWellnessPricesParams = {
  universe: Universe;
  zone: ZoneId;
  primaryAppointment: Moment | null;
  basketContent: BasketContent;
  minPriceSurchargeAccepted: boolean;
  comboPrices: ComboPrice[];
  tipAmount?: number;
  promotion?: Promotion | null;
  giftCard?: GiftCard;
  membershipComboPrices: ComboPrice[];
};

export const computeWellnessPrices = ({
  universe,
  zone,
  primaryAppointment,
  basketContent,
  comboPrices,
  minPriceSurchargeAccepted,
  tipAmount = 0,
  promotion,
  giftCard,
  membershipComboPrices,
}: ComputeWellnessPricesParams): WellnessBasketPrices => {
  const { minimum_price: minimumPrice, membership_minimum_price: membershipMinimumPrice, holidays } = universe;

  const basketContentWithPrices = basketContent.map((category) =>
    basketContentPrices(category, universe, zone, comboPrices, membershipComboPrices),
  );

  // Total price whithout adjustments
  const prestationsPrice = Object.values(basketContentWithPrices).reduce(
    (sum, categoryPrices) => ({
      standard: sum.standard + categoryPrices.price.standard,
      reduced: sum.reduced + categoryPrices.price.reduced,
      membership: sum.membership + (categoryPrices.price.membership || 0),
      membershipReduced: sum.membershipReduced + (categoryPrices.price.membershipReduced || 0),
    }),
    {
      standard: 0,
      reduced: 0,
      membership: 0,
      membershipReduced: 0,
    },
  );

  // Appointment surcharge
  const appointmentSurchargeReason = getAppointmentSurchargeReason(primaryAppointment, holidays);
  const appointmentSurcharge =
    prestationsPrice.reduced > 0 && appointmentSurchargeReason ? PRESTATIONS_BASED_SURCHARGE : 0;
  const membershipAppointmentSurcharge =
    prestationsPrice.membershipReduced > 0 && appointmentSurchargeReason ? MEMBERSHIP_PRESTATIONS_BASED_SURCHARGE : 0;

  // Minimum Price surcharge
  const prestationsAppointmentPrice = prestationsPrice.reduced + appointmentSurcharge;
  const minPriceSurcharge =
    prestationsAppointmentPrice > 0 && prestationsAppointmentPrice < minimumPrice && minPriceSurchargeAccepted
      ? minimumPrice - prestationsAppointmentPrice
      : 0;
  const membershipPrestationsAppointmentPrice = prestationsPrice.membershipReduced + membershipAppointmentSurcharge;
  const membershipMinPriceSurcharge =
    membershipPrestationsAppointmentPrice > 0 &&
    membershipPrestationsAppointmentPrice < membershipMinimumPrice &&
    minPriceSurchargeAccepted
      ? membershipMinimumPrice - membershipPrestationsAppointmentPrice
      : 0;

  // Total before applying promo code or gift card
  const totalPriceBeforeReductions = prestationsPrice.reduced + appointmentSurcharge + minPriceSurcharge;
  const membershipTotalPriceBeforeReductions =
    prestationsPrice.membershipReduced + membershipAppointmentSurcharge + membershipMinPriceSurcharge;

  // Reductions
  const membershipReduction = totalPriceBeforeReductions - membershipTotalPriceBeforeReductions;

  // Promo and gift card values
  const { promoValue, giftValue } = calculatePromoAndGiftValues({
    priceBeforeReduction: totalPriceBeforeReductions,
    promotion,
    giftCard,
  });
  const { promoValue: membershipPromoValue } = calculatePromoAndGiftValues({
    priceBeforeReduction: membershipTotalPriceBeforeReductions,
    promotion,
    giftCard,
  });

  const promoGiftReductions = promoValue + giftValue;
  const membershipPromoGiftReductions = membershipPromoValue + giftValue;

  // Total
  const totalPrice = Math.max(totalPriceBeforeReductions - promoGiftReductions, 0);
  const membershipTotalPrice = Math.max(membershipTotalPriceBeforeReductions - membershipPromoGiftReductions, 0);

  return {
    kind: 'wellness',
    totalPrice: round(totalPrice),
    minPriceSurcharge: round(minPriceSurcharge),
    appointmentSurcharge: round(appointmentSurcharge),
    appointmentSurchargeReason,
    promoValue: round(promoValue),
    giftValue: round(giftValue),
    tipAmount: round(tipAmount),
    comboPriceReduction: round(prestationsPrice.standard - prestationsPrice.reduced),
    basketContentWithPrices,
    membership: {
      reduction: round(membershipReduction),
      totalPrice: round(membershipTotalPrice),
      minPriceSurcharge: round(membershipMinPriceSurcharge),
      appointmentSurcharge: round(membershipAppointmentSurcharge),
      promoValue: round(membershipPromoValue),
      giftValue: round(giftValue),
      comboPriceReduction: round(prestationsPrice.membership - prestationsPrice.membershipReduced),
    },
  };
};
